/**
 * `pinnedViews` (docs/ops-protocol.md, "Scroll geometry, pinned views and
 * animation completion"): a lazy stack or grid marked `pinned` keeps each
 * `section` child's header at the leading edge of the scroll view's visible
 * area while the section passes, and the next section's header pushes it
 * out, as `LazyVStack(pinnedViews: [.sectionHeaders])` does; a pinned footer
 * stays at the trailing edge while its section is in view the same way.
 *
 * The renderer registers the marked stacks; after every layout and on every
 * scroll event each header is moved by a transform along the axis. The
 * laid-out positions are left alone (offsets, not client rects), so the
 * transform never feeds back into where the header is reckoned to be.
 *
 * Where the browser has scroll-driven animations, a header is instead moved
 * by one (styles.css `sb-pinned`) along its scroll view's timeline, from the
 * scroll offset where it starts to pin to the one where its section's end
 * stops it. The browser then moves it in step with the scroll itself, where a
 * scroll event, a frame late on iOS, would leave it jittering.
 */

import type { PinnedView } from './protocol';
import { layoutOrigin } from './scrollTransition';

export interface Span {
  start: number;
  length: number;
}

/**
 * How far a header moves toward the trailing edge to stay at `viewStart`:
 * nothing while its section's start is in view, then as far as the view has
 * scrolled past it, and no further than its section's end (where the next
 * section's header pushes it out).
 */
export function pinnedHeaderOffset(viewStart: number, header: Span, sectionEnd: number): number {
  const room = Math.max(0, sectionEnd - header.start - header.length);
  return Math.min(room, Math.max(0, viewStart - header.start));
}

/**
 * How far a footer moves toward the leading edge (negative) to stay at
 * `viewEnd` while its section is in view: no further than its section's
 * start, where the previous section's footer takes over.
 */
export function pinnedFooterOffset(viewEnd: number, footer: Span, sectionStart: number): number {
  const room = Math.min(0, sectionStart - footer.start);
  return Math.max(room, Math.min(0, viewEnd - footer.start - footer.length));
}

const registered = new Map<HTMLElement, Set<PinnedView>>();

/** Whether headers pin by a scroll-driven animation (styles.css), not by script on each scroll event. */
const scrollDriven = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('animation-timeline', '--sb-pin-y');

/** Called by the renderer for every stack and grid: registers, updates or forgets its pinned views. */
export function setPinnedViews(el: HTMLElement, pinned: PinnedView[] | undefined): void {
  const views = new Set<PinnedView>(Array.isArray(pinned) ? pinned.filter((view) => view === 'sectionHeaders' || view === 'sectionFooters') : []);
  if (views.size === 0) {
    if (registered.delete(el)) {
      delete el.dataset['sbPinned'];
      for (const section of sections(el)) {
        for (const kind of ['sectionheader', 'sectionfooter'] as const) {
          const view = supplement(section, kind);
          if (view) {
            view.style.transform = '';
            view.style.zIndex = '';
            unpin(view);
          }
        }
      }
    }
    return;
  }
  registered.set(el, views);
  el.dataset['sbPinned'] = [...views].join(' ');
  draw(el, views);
}

function sections(stack: HTMLElement): HTMLElement[] {
  return [...stack.children].filter((child): child is HTMLElement => child instanceof HTMLElement && child.classList.contains('sb-section'));
}

function supplement(section: HTMLElement, kind: 'sectionheader' | 'sectionfooter'): HTMLElement | null {
  for (const child of section.children) {
    if (child instanceof HTMLElement && child.classList.contains(`sb-${kind}`)) return child;
  }
  return null;
}

/** The inset of a bar the scroll view runs under (largeTitle.ts sets `--sb-top-inset`). */
function topInset(scroller: HTMLElement): number {
  return parseFloat(scroller.style.getPropertyValue('--sb-top-inset')) || 0;
}

function draw(stack: HTMLElement, views: Set<PinnedView>): void {
  const scroller = stack.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list');
  if (!scroller) return;
  const horizontal = stack.classList.contains('sb-hstack') || stack.classList.contains('sb-lazyhgrid');
  const viewStart = horizontal ? scroller.scrollLeft : scroller.scrollTop + topInset(scroller);
  const viewEnd = (horizontal ? scroller.scrollLeft + scroller.clientWidth : scroller.scrollTop + scroller.clientHeight);
  for (const section of sections(stack)) {
    const origin = layoutOrigin(section, scroller);
    if (!origin) continue;
    const sectionStart = horizontal ? origin.x : origin.y;
    const sectionEnd = sectionStart + (horizontal ? section.offsetWidth : section.offsetHeight);
    if (views.has('sectionHeaders')) {
      const header = supplement(section, 'sectionheader');
      if (header) {
        const at = layoutOrigin(header, scroller);
        const span: Span = { start: horizontal ? (at?.x ?? sectionStart) : (at?.y ?? sectionStart), length: horizontal ? header.offsetWidth : header.offsetHeight };
        if (scrollDriven) pin(header, horizontal, span.start - (horizontal ? 0 : topInset(scroller)), pinnedHeaderOffset(Infinity, span, sectionEnd));
        else move(header, horizontal, pinnedHeaderOffset(viewStart, span, sectionEnd));
      }
    }
    if (views.has('sectionFooters')) {
      const footer = supplement(section, 'sectionfooter');
      if (footer) {
        const at = layoutOrigin(footer, scroller);
        const span: Span = { start: horizontal ? (at?.x ?? sectionEnd) : (at?.y ?? sectionEnd), length: horizontal ? footer.offsetWidth : footer.offsetHeight };
        move(footer, horizontal, pinnedFooterOffset(viewEnd, span, sectionStart));
      }
    }
  }
}

function move(view: HTMLElement, horizontal: boolean, offset: number): void {
  const rounded = Math.round(offset * 100) / 100;
  view.style.transform = rounded === 0 ? '' : horizontal ? `translateX(${rounded}px)` : `translateY(${rounded}px)`;
  // Over the rows of its section, which come after it in the stack.
  view.style.zIndex = '1';
}

/**
 * Pins a header by a scroll-driven animation: from the scroll offset `from`, where its view's start reaches it, it
 * moves with the scroll for `room` and stays there. The scroll view names its timelines (styles.css).
 */
function pin(view: HTMLElement, horizontal: boolean, from: number, room: number): void {
  view.style.transform = '';
  view.style.zIndex = '1';
  if (room <= 0) {
    unpin(view);
    return;
  }
  const start = Math.round(from * 100) / 100;
  const end = Math.round((from + room) * 100) / 100;
  view.style.animation = 'sb-pinned linear both';
  view.style.setProperty('animation-timeline', horizontal ? '--sb-pin-x' : '--sb-pin-y');
  view.style.setProperty('animation-range', `${start}px ${end}px`);
  view.style.setProperty('--sb-pinned-to', horizontal ? `${room}px 0` : `0 ${room}px`);
}

function unpin(view: HTMLElement): void {
  view.style.animation = '';
  view.style.removeProperty('animation-timeline');
  view.style.removeProperty('animation-range');
  view.style.removeProperty('--sb-pinned-to');
}

/** Moves every pinned view to its place: after a layout, and on every scroll event. */
export function drawPinnedViews(): void {
  for (const [stack, views] of registered) {
    if (!stack.isConnected) {
      registered.delete(stack);
      continue;
    }
    draw(stack, views);
  }
}
